import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { plants } from "@/lib/api";
import { Camera, Leaf, Trash2, Search, X, ShieldAlert, Sprout, CheckSquare, WifiOff, Copy, Share2, Tag, Layers, Pencil, Loader2 } from "lucide-react";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import ShareDialog from "@/components/ShareDialog";
import PlantDetailPane from "@/components/PlantDetailPane";
import BulkTagDialog from "@/components/BulkTagDialog";
import BulkCorridorDialog from "@/components/BulkCorridorDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import useGalleryScoreboard from "@/hooks/useGalleryScoreboard";
import useGalleryFilteredSorted from "@/hooks/useGalleryFilteredSorted";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { idNative, idEndangered } from "@/lib/idCompat";
import { localizedIdentification } from "@/lib/localizedId";
import { useAuth } from "@/contexts/AuthContext";
import {
  FILTER_KEYS,
  PAGE_SIZE_OPTIONS,
  PAGE_SIZE_STORAGE_KEY,
  DEFAULT_PAGE_SIZE,
  SORT_KEYS,
  SORT_KEYS_SECONDARY,
  SORT_STORAGE_KEY,
  SORT_STORAGE_KEY_SECONDARY,
  DEFAULT_SORT,
  DEFAULT_SORT_SECONDARY,
  SORT_FIELD_GROUP,
  secondarySortOptionsFor,
  isUnidentified,
  normalize,
  buildSearchHaystack,
  matchesFilters as matchesFiltersBase,
  EXCLUSIVE_PAIRS,
} from "@/lib/galleryUtils";
import { PlantCard } from "@/components/gallery/PlantCard";
import { Pagination } from "@/components/gallery/Pagination";
import { Stat } from "@/components/gallery/Stat";
import { ActionBtn } from "@/components/gallery/ActionBtn";
import { SkeletonGrid, EmptyState, NoMatch } from "@/components/gallery/GalleryStates";
import { MergePickerDialog } from "@/components/gallery/MergePickerDialog";
import { GalleryMobileControls } from "@/components/gallery/GalleryMobileControls";
import { GallerySelectActionBar } from "@/components/gallery/GallerySelectActionBar";
import { GalleryDialogs } from "@/components/gallery/GalleryDialogs";
import useGalleryOfflineLoader from "@/hooks/useGalleryOfflineLoader";
import { useGalleryDesktopMasterDetail, useGalleryDesktopKeyboardNav } from "@/hooks/useGalleryDesktopMasterDetail";
import useGalleryMerge from "@/hooks/useGalleryMerge";

// Local wrapper so callers don't need to pass idNative/idEndangered on every invocation.
const matchesFilters = (item, id, keys) => matchesFiltersBase(item, id, keys, idNative, idEndangered);


export default function Gallery() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // react-router-dom v7 mints a fresh `setSearchParams` identity on every URL
  // change (its useCallback closes over the current `searchParams`). Any
  // effect that lists `setSearchParams` in its deps therefore re-fires on
  // EVERY unrelated URL write — and if it unconditionally writes back, it
  // can resurrect params another effect just deleted (page-reset), or wipe
  // params another effect just wrote (`?selected` for the desktop pane).
  // Route every effect's URL writes through this stable ref instead.
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => { setSearchParamsRef.current = setSearchParams; }, [setSearchParams]);

  // Active tag filter — URL-driven via `?tags=a,b` so it survives refresh /
  // back-forward and can be shared. AND semantics: an item matches only if
  // it carries ALL selected tags. Empty set = no tag filter.
  const activeTags = useMemo(() => {
    const raw = searchParams.get("tags");
    if (!raw) return new Set();
    return new Set(
      raw.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean)
    );
  }, [searchParams]);
  const setActiveTags = useCallback((next) => {
    // Atomic write: tag delta + page reset in ONE setSearchParams call so
    // there's no race with a separate reset effect. (See iter43 note above.)
    // Route through the stable ref so this callback's identity doesn't
    // churn on every URL write (react-router v7 setSearchParams identity
    // rotates per commit).
    setSearchParamsRef.current((prev) => {
      const q = new URLSearchParams(prev);
      if (next.size === 0) q.delete("tags");
      else q.set("tags", Array.from(next).join(","));
      q.delete("page");
      return q;
    }, { replace: true });
  }, []);
  const toggleTag = useCallback((tag) => {
    const key = String(tag || "").trim().toLowerCase();
    if (!key) return;
    const next = new Set(activeTags);
    if (next.has(key)) next.delete(key); else next.add(key);
    setActiveTags(next);
  }, [activeTags, setActiveTags]);
  const clearTags = useCallback(() => setActiveTags(new Set()), [setActiveTags]);
  // Collapse the chip row to the top-N most-used tags by default; a power
  // user with 40 tags can expand to see all of them. Active tags are always
  // visible (the collapse filter keeps them in) so a URL-shared link with
  // `?tags=obscure` still shows that chip pressed.
  const [tagsExpanded, setTagsExpanded] = useState(false);
  // "Manage" mode on the tag filter row — toggled by the pencil affordance
  // next to the "Tags:" label. In manage mode, each chip becomes a tap-to-
  // delete target (with a confirm) that removes the tag from EVERY scan the
  // user owns (`DELETE /api/plants/tags/{tag}`). Kept separate from active
  // filter selection so a user can't accidentally delete a tag they were
  // trying to filter by.
  const [tagsManageMode, setTagsManageMode] = useState(false);
  const [tagDeletePending, setTagDeletePending] = useState(null); // {key,label}
  const [tagDeleteBusy, setTagDeleteBusy] = useState(false);

  const { items, setItems, offline, load } = useGalleryOfflineLoader(user?.user_id, t);
  // NOTE: must be declared AFTER useGalleryOfflineLoader — it closes over
  // `setItems` (TDZ crash otherwise).
  const confirmDeleteTagGlobally = useCallback(async () => {
    const pending = tagDeletePending;
    if (!pending) return;
    setTagDeleteBusy(true);
    try {
      const { data } = await plants.deleteTag(pending.label);
      // Optimistic: strip the tag from every item in memory so both the
      // filter chip row and any visible card's tag list update instantly.
      setItems((prev) => (prev || []).map((it) => {
        const kept = (it.tags || []).filter(
          (x) => String(x).toLowerCase() !== pending.key
        );
        if (kept.length === (it.tags || []).length) return it;
        return { ...it, tags: kept };
      }));
      // Also drop it from active filters so we don't leave a stale ?tags=
      setActiveTags((prev) => {
        if (!prev.has(pending.key)) return prev;
        const next = new Set(prev);
        next.delete(pending.key);
        return next;
      });
      toast.success(
        t("gallery.tagDeleteToast", { tag: pending.label, n: data?.scan_count ?? 0 })
      );
      setTagDeletePending(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("gallery.tagDeleteFail"));
    } finally {
      setTagDeleteBusy(false);
    }
  }, [tagDeletePending, setItems, setActiveTags, t]);
  // Mount-skip ref used by every URL-sync effect below. On first render we
  // just record that we've mounted — we don't want to touch the URL (e.g. we
  // don't want to drop `?page=N` that just came in from a back-nav).
  const urlSyncMounted = useRef(false);
  useEffect(() => { urlSyncMounted.current = true; }, []);

  // Search query lives in the URL as `?q=<value>` so back-navigation from a
  // plant detail preserves the user's search. `useReturnableRoute` captures
  // `location.search`, so as long as `q` is in the query string we round-trip
  // correctly. We keep a local mirror to avoid a URL churn on every keystroke
  // and debounce the write so history stays clean (`replace: true`, no new
  // history entries per character).
  //
  // This effect is the ONE place that writes q + resets page for the search
  // input. Consolidating those two writes into a single setSearchParams call
  // avoids the race with any concurrent effect (iter43: two updater-form
  // setSearchParams calls in the same commit still race because both read the
  // same committed history entry).
  const [query, setQuery] = useState(() => searchParams.get("q") || "");
  useEffect(() => {
    const current = searchParams.get("q") || "";
    if (current === query) return;
    const handle = setTimeout(() => {
      setSearchParamsRef.current((prev) => {
        const next = new URLSearchParams(prev);
        if (query) next.set("q", query);
        else next.delete("q");
        if (urlSyncMounted.current) next.delete("page");
        return next;
      }, { replace: true });
    }, 250);
    return () => clearTimeout(handle);
  }, [query, searchParams]);
  // Multi-select filter: user can activate any combination of category chips
  // and results are AND'd. Empty set = "all". Persisted as a comma-separated
  // list in the ?filter= URL param so shareable/bookmarked links preserve the
  // exact filter combination.
  const [filters, setFilters] = useState(() => {
    const p = searchParams.get("filter") || "";
    const parts = p.split(",").map((s) => s.trim()).filter(Boolean);
    return new Set(parts.filter((k) => FILTER_KEYS.includes(k) && k !== "all"));
  });
  // Keep the `filter` in the URL AND drop `page` in the same call so a filter
  // toggle atomically resets pagination. iter43 tried to fix this with the
  // functional updater form alone but the concurrent setPage(0) effect still
  // clobbered the filter write — react-router-dom v6 batches route updates
  // such that both updaters see the same committed history entry.
  useEffect(() => {
    const desiredArr = Array.from(filters);
    desiredArr.sort();
    const desired = desiredArr.join(",");
    const currentRaw = searchParams.get("filter") || "";
    // Bail out with NO setSearchParams call at all when there's no filter
    // delta AND we've already mounted — a stale-snapshot write here would
    // resurrect params another effect just deleted (e.g. `?page` after a
    // pageSize reset). iter50 root-caused the ?page=2 resurrection to
    // exactly this call site running with a stale `searchParams` snapshot.
    if (currentRaw === desired && urlSyncMounted.current) return;
    setSearchParamsRef.current((prev) => {
      const prevRaw = prev.get("filter") || "";
      if (prevRaw === desired && urlSyncMounted.current) return prev;
      const next = new URLSearchParams(prev);
      if (!desired) next.delete("filter");
      else next.set("filter", desired);
      if (urlSyncMounted.current) next.delete("page");
      return next;
    }, { replace: true });
  }, [filters, searchParams]);
  // Backward-compat helpers: many render / analytics paths still refer to a
  // single filter concept. When exactly one filter is active, mirror it here
  // so existing UI (empty-state copy, chip highlighting) reads naturally.
  const toggleFilter = (key) => {
    setFilters((prev) => {
      const next = new Set(prev);
      if (key === "all") { next.clear(); return next; }
      if (next.has(key)) {
        next.delete(key);
        return next;
      }
      // Adding a chip — silently remove any chips that are mutually exclusive
      // with it. Collect the removed labels so we can surface a one-line toast
      // that explains why the previously-active chip vanished; a silent
      // auto-remove would read as a bug rather than helpful behavior.
      const conflicts = (EXCLUSIVE_PAIRS[key] || []).filter((k) => next.has(k));
      conflicts.forEach((k) => next.delete(k));
      if (conflicts.length > 0) {
        // Fire outside the setter so React doesn't get a rendering-side-effect
        // warning. Sonner handles queuing itself.
        setTimeout(() => {
          const removedLabel = conflicts
            .map((k) => t(`gallery.filters.${k}`))
            .join(", ");
          const addedLabel = t(`gallery.filters.${key}`);
          toast.message(t("gallery.filterAutoSwap", {
            removed: removedLabel,
            added: addedLabel,
          }));
        }, 0);
      }
      next.add(key);
      return next;
    });
  };
  const clearFilters = () => setFilters(new Set());
  const noFiltersActive = filters.size === 0;
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [pendingBulkMerge, setPendingBulkMerge] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareInitialScope, setShareInitialScope] = useState("all");
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [bulkCorridorOpen, setBulkCorridorOpen] = useState(false);
  const [hintShown, setHintShown] = useState(false);
  const [duplicateGroupCount, setDuplicateGroupCount] = useState(0);

  // Deep-link handoff from /corridor "Publish more" (?preselect=corridor).
  // Fetches the caller's already-located but not-yet-published scans, enters
  // select mode, preselects them, opens the Bulk Corridor dialog, and strips
  // the query param so a shallow reload / back-forward doesn't retrigger.
  // Runs once per landing thanks to the `preselect` param being read fresh
  // on each render — after we clear it, subsequent renders skip the effect.
  useEffect(() => {
    if (searchParams.get("preselect") !== "corridor") return;
    let cancelled = false;
    (async () => {
      try {
        const { corridor: corridorApi } = await import("@/lib/corridorApi");
        const { data } = await corridorApi.eligible();
        if (cancelled) return;
        const ids = (data?.items || []).map((i) => i.scan_id).filter(Boolean);
        if (ids.length === 0) {
          // Nothing to publish — probably a stale deep-link. Just clean up.
          const next = new URLSearchParams(searchParams);
          next.delete("preselect");
          setSearchParams(next, { replace: true });
          toast.message(t("gallery.preselectCorridorEmpty"));
          return;
        }
        setSelectMode(true);
        setSelected(new Set(ids));
        setBulkCorridorOpen(true);
        const next = new URLSearchParams(searchParams);
        next.delete("preselect");
        setSearchParams(next, { replace: true });
      } catch {
        // Best-effort — don't block Gallery over a failed preselect.
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams, t]);

  // Load the duplicate-group count once the gallery is populated.
  useEffect(() => {
    if (!items || items.length < 2) { setDuplicateGroupCount(0); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await plants.duplicates();
        if (!cancelled) setDuplicateGroupCount(data?.group_count || 0);
      } catch {
        if (!cancelled) setDuplicateGroupCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [items]);

  // Show a one-time hint about long-press to delete, on the first gallery view that has cards.
  useEffect(() => {
    try {
      if (items && items.length > 0 && !hintShown && !localStorage.getItem("prairie.gallery.hintShown")) {
        toast.message(t("gallery.firstHint"), { duration: 6000 });
        localStorage.setItem("prairie.gallery.hintShown", "1");
        setHintShown(true);
      }
    } catch { /* storage blocked — skip the hint */ }
  }, [items, hintShown, t]);

  const [pageSize, setPageSize] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem(PAGE_SIZE_STORAGE_KEY), 10);
      return PAGE_SIZE_OPTIONS.includes(saved) ? saved : DEFAULT_PAGE_SIZE;
    } catch {
      return DEFAULT_PAGE_SIZE;
    }
  });
  const [sortKey, setSortKey] = useState(() => {
    try {
      const saved = localStorage.getItem(SORT_STORAGE_KEY);
      return SORT_KEYS.includes(saved) ? saved : DEFAULT_SORT;
    } catch {
      return DEFAULT_SORT;
    }
  });
  const [sortKey2, setSortKey2] = useState(() => {
    try {
      const saved = localStorage.getItem(SORT_STORAGE_KEY_SECONDARY);
      return SORT_KEYS_SECONDARY.includes(saved) ? saved : DEFAULT_SORT_SECONDARY;
    } catch {
      return DEFAULT_SORT_SECONDARY;
    }
  });
  useEffect(() => {
    try { localStorage.setItem(SORT_STORAGE_KEY_SECONDARY, sortKey2); } catch { /* ignore quota */ }
  }, [sortKey2]);
  // Whenever the primary sort changes, snap the secondary back to "none" if
  // the two now share an underlying field (e.g. primary=date_new + saved
  // secondary=date_old). Prevents a nonsensical "newest, then oldest"
  // combination that could persist across sessions via localStorage.
  useEffect(() => {
    const primaryGroup = SORT_FIELD_GROUP[sortKey];
    const secondaryGroup = SORT_FIELD_GROUP[sortKey2];
    if (primaryGroup && secondaryGroup && primaryGroup === secondaryGroup) {
      setSortKey2(DEFAULT_SORT_SECONDARY);
    }
  }, [sortKey, sortKey2]);
  const secondaryOptions = secondarySortOptionsFor(sortKey);
  // Page lives in the URL as `?page=N` (1-indexed for user-friendly links)
  // so back-navigation from a plant detail preserves the page the user was
  // on. `useReturnableRoute` captures `location.search`, so as long as the
  // page number is in the query string we round-trip correctly. Internally
  // we keep the 0-indexed convention the rest of the code was built on.
  const page = useMemo(() => {
    const raw = parseInt(searchParams.get("page") || "1", 10);
    return Number.isFinite(raw) && raw >= 1 ? raw - 1 : 0;
  }, [searchParams]);
  const setPage = useCallback((updater) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      const currentZero = Math.max(0, (parseInt(prev.get("page") || "1", 10) || 1) - 1);
      const targetZero = typeof updater === "function" ? updater(currentZero) : updater;
      if (!Number.isFinite(targetZero) || targetZero <= 0) next.delete("page");
      else next.set("page", String(targetZero + 1));
      return next;
    }, { replace: true });
  }, []);

  // Master-detail helpers for the desktop layout. `isDesktop`, `selectedId`
  // and the open/close/scroll-into-view effects live in the shell half of
  // `useGalleryDesktopMasterDetail`; the keyboard-nav half is called later
  // (once `filtered` is known). Behavior-preserving iter56 extraction.
  const {
    isDesktop, selectedId, openInline, closeInline, closeInlineRef,
  } = useGalleryDesktopMasterDetail({ searchParams, setSearchParams, items });
  const selectedScan = useMemo(() => {
    if (!selectedId || !items) return null;
    return items.find((it) => it.scan_id === selectedId) || null;
  }, [selectedId, items]);

  const chipRefs = useRef({});
  const topRef = useRef(null);

  const searchInputRef = useRef(null);

  // Auto-hide the mobile keyboard once the user pauses typing so they can see
  // the filtered results without manually dismissing. Also blur on Enter and
  // on the first scroll after typing (native-app-style dismiss-on-drag).
  useEffect(() => {
    if (!query) return;
    const inputEl = searchInputRef.current;
    if (!inputEl || document.activeElement !== inputEl) return;
    const timer = setTimeout(() => {
      if (document.activeElement === inputEl) inputEl.blur();
    }, 900);
    const onScroll = () => {
      if (document.activeElement === inputEl) inputEl.blur();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [query]);

  useEffect(() => {
    // Scroll the most recently activated chip into view. When multi-select is
    // in play we just pick the "highest priority" one — 'all' when the set is
    // empty, or the newest addition otherwise.
    //
    // Page reset used to live here too, but that caused a race with the
    // filter URL-sync effect (both wrote setSearchParams in the same commit,
    // second call clobbered the first). Page reset is now folded directly
    // into the filter URL-sync effect above — one atomic write per user
    // action.
    const activeKey = filters.size === 0
      ? "all"
      : Array.from(filters)[filters.size - 1];
    const el = chipRefs.current[activeKey];
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [filters]);

  // Reset to page 0 when local (non-URL) state changes that affect ordering
  // or paging: pageSize + sortKey. `query` and `activeTags` already handle
  // page reset inline in their URL writes (see comments above). Skip on
  // mount so a back-nav's `?page=N` doesn't get wiped before it's applied.
  //
  // We write `page` AND `selected` in ONE atomic setSearchParams updater so
  // the pagination-scroll effect below doesn't need a separate closeInline()
  // (a second write in the same commit would clobber this one — both would
  // read the same committed history entry).
  // StrictMode-safe "did the input actually change since mount?" guard.
  // A boolean `hasMounted` ref is defeated by React 19 StrictMode's passive-
  // effect double-invoke: on the second run `hasMounted.current` is already
  // true, so the reset write fires and strips `?page`/`?selected` from a
  // deep-linked URL. Snapshot-comparison is stable across double-invokes.
  const initialPagingRef = useRef({ pageSize, sortKey });
  useEffect(() => {
    if (initialPagingRef.current.pageSize === pageSize
        && initialPagingRef.current.sortKey === sortKey) return;
    initialPagingRef.current = { pageSize, sortKey };
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("page");
      next.delete("selected");
      return next;
    }, { replace: true });
  }, [pageSize, sortKey]);

  // Ref flipped by useGalleryDesktopKeyboardNav right before an Arrow-key
  // page jump. The pagination-scroll effect below consumes it so it can
  // scroll+focus the header WITHOUT calling closeInline() (which would
  // clobber the ?selected write the keyboard hook just made in the same
  // commit).
  const keyboardNavRef = useRef(false);

  // Scroll to top whenever page changes (or page-size changes). Also close
  // the master-detail pane on a pure page change — its selection references
  // a scan on the previous page. StrictMode-safe: no "hasMounted" flag; we
  // snapshot the initial page/pageSize and only act when they actually
  // change since mount. This also fixes a deep-link race where the first
  // effect invoke would scroll the sticky-header topRef out of view.
  const initialPageStateRef = useRef({ page, pageSize });
  useEffect(() => {
    // No genuine change since mount (also covers StrictMode double-invoke).
    if (initialPageStateRef.current.page === page
        && initialPageStateRef.current.pageSize === pageSize) return;
    const pageSizeChanged = initialPageStateRef.current.pageSize !== pageSize;
    initialPageStateRef.current = { page, pageSize };

    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      // A11y: move focus to the heading landmark so keyboard/AT users don't
      // lose focus to <body> when Prev/Next becomes disabled at an end.
      topRef.current.focus({ preventScroll: true });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    // Keyboard-initiated page jumps have already written the desired ?selected
    // in the same commit — closing here would wipe it.
    if (keyboardNavRef.current) {
      keyboardNavRef.current = false;
      return;
    }
    // On a pageSize change the page-reset effect above already atomically
    // deleted `selected` in the SAME commit. A second setSearchParams call
    // here would run with a STALE `prev` snapshot (react-router v7's
    // functional-updater `prev` reflects the last committed URL, not any
    // pending navigate scheduled in the same tick) and resurrect `?page`.
    // Skip the redundant close on that path — the atomic write already
    // did the right thing.
    if (pageSizeChanged) return;
    closeInlineRef.current?.();
  }, [page, pageSize, closeInlineRef]);

  // Wire the "Undo" action button on delete/bulk-delete success toasts.
  // `preItems` is a local snapshot of the item list before the mutation so the
  // UI can restore instantly on undo (backend restores the docs regardless).
  const showDeleteUndoToast = (message, snapshotId, preItems) => {
    if (!snapshotId) { toast.success(message); return; }
    toast.success(message, {
      duration: 10_000,
      action: {
        label: t("common.undo"),
        onClick: async () => {
          try {
            await plants.undo(snapshotId);
            setItems(preItems);
            toast.success(t("gallery.toastDeleteUndone"));
          } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(detail === "undo_expired" ? t("gallery.toastUndoExpired") : t("gallery.toastUndoFail"));
          }
        },
      },
    });
  };

  const remove = async (id) => {
    const preItems = items;
    try {
      const { data } = await plants.remove(id);
      setItems((prev) => prev.filter((i) => i.scan_id !== id));
      showDeleteUndoToast(t("gallery.toastRemoved"), data?.snapshot_id, preItems);
    } catch {
      toast.error(t("gallery.toastDeleteFail"));
    } finally {
      setPendingDelete(null);
    }
  };

  const bulkRemove = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) { setPendingBulkDelete(false); return; }
    const preItems = items;
    try {
      const { data } = await plants.bulkRemove(ids);
      setItems((prev) => prev.filter((i) => !selected.has(i.scan_id)));
      showDeleteUndoToast(t("gallery.toastBulkRemoved", { n: data.deleted ?? ids.length }), data?.snapshot_id, preItems);
      setSelected(new Set());
      setSelectMode(false);
    } catch {
      toast.error(t("gallery.toastDeleteFail"));
    } finally {
      setPendingBulkDelete(false);
    }
  };

  // Merge from bulk-select — handled by `useGalleryMerge` (iter56 extraction).
  // Owns the picker state, the "do we need the photo picker?" fast path,
  // and the actual `plants.merge()` call + optimistic list update + Undo toast.
  const {
    merging,
    pickerOpen,
    pickerLoading,
    pickerCandidates,
    pickerSelection,
    pickerPrimary,
    setPickerOpen,
    handleMergeIntent,
    togglePickerCandidate,
    bulkMerge,
  } = useGalleryMerge({
    items,
    selected,
    setItems,
    setSelected,
    setSelectMode,
    setPendingBulkMerge,
  });

  // Marks a list of scans as reviewed (clears needs_revisit). Used by both
  // the bulk-select toolbar and the "Mark all visible reviewed" header button.
  const [pendingMarkReviewed, setPendingMarkReviewed] = useState(null); // { ids, source }
  const markReviewedRun = async () => {
    const req = pendingMarkReviewed;
    if (!req || req.ids.length === 0) { setPendingMarkReviewed(null); return; }
    try {
      const { data } = await plants.bulkReview(req.ids, true);
      const idSet = new Set(req.ids);
      setItems((prev) => prev.map((i) => idSet.has(i.scan_id) ? { ...i, needs_revisit: false } : i));
      toast.success(t("gallery.toastMarkedReviewed", { n: data.updated ?? req.ids.length }));
      if (req.source === "bulk") {
        setSelected(new Set());
        setSelectMode(false);
      }
    } catch {
      toast.error(t("gallery.toastMarkFail"));
    } finally {
      setPendingMarkReviewed(null);
    }
  };

  const { scoreboard, filterCounts } = useGalleryScoreboard(items, user?.language);

  const filtered = useGalleryFilteredSorted({
    items,
    query,
    filters,
    activeTags,
    sortKey,
    sortKey2,
    userLanguage: user?.language,
  });

  // Aggregate the user's tags across ALL of their scans (not just the current
  // filter) so the chip row is stable — activating a chip won't remove others.
  // Sorted by frequency desc, then alphabetical for stable order.
  const availableTags = useMemo(() => {
    if (!items || items.length === 0) return [];
    const counts = new Map();
    const display = new Map();
    for (const it of items) {
      for (const raw of (it?.tags || [])) {
        const s = String(raw || "").trim();
        if (!s) continue;
        const key = s.toLowerCase();
        counts.set(key, (counts.get(key) || 0) + 1);
        display.set(key, s);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([key, count]) => ({ key, label: display.get(key) || key, count }));
  }, [items]);

  const paged = useMemo(() => {
    if (!filtered) return { items: null, totalPages: 1 };
    if (pageSize === 0) return { items: filtered, totalPages: 1 };
    const total = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, total - 1);
    const start = safePage * pageSize;
    return { items: filtered.slice(start, start + pageSize), totalPages: total };
  }, [filtered, page, pageSize]);

  // Keyboard navigation (Arrow/Escape) for the desktop detail pane — the
  // second half of `useGalleryDesktopMasterDetail`. Runs here, after
  // `filtered/page/pageSize/setPage` are all defined.
  useGalleryDesktopKeyboardNav({
    isDesktop, selectedId, filtered, page, pageSize, openInline, closeInline, keyboardNavRef,
  });

  const toggleSelect = (scanId) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(scanId)) n.delete(scanId); else n.add(scanId);
      return n;
    });
  };
  const selectAllOnPage = () => {
    const ids = (paged.items || []).map((i) => i.scan_id);
    const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
    setSelected((s) => {
      const n = new Set(s);
      if (allSelected) ids.forEach((id) => n.delete(id));
      else ids.forEach((id) => n.add(id));
      return n;
    });
  };
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };

  const setSortAndPersist = (key) => {
    setSortKey(key);
    try { localStorage.setItem(SORT_STORAGE_KEY, key); }
    catch (e) { if (process.env.NODE_ENV !== "production") console.warn("[gallery] could not persist sort:", e); }
  };

  const setPageSizeAndPersist = (n) => {
    setPageSize(n);
    try { localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(n)); }
    catch (e) { if (process.env.NODE_ENV !== "production") console.warn("[gallery] could not persist pageSize:", e); }
  };

  return (
    <AppShell>
      <span ref={topRef} tabIndex={-1} className="sr-only outline-none">{t("gallery.title")}</span>
      {offline && (
        <div className="mx-5 mt-2 rounded-2xl bg-[#FFF4DC] border border-[#E9C46A]/40 px-3 py-2 text-[12px] text-[#8a6a14] flex items-center gap-2" data-testid="offline-banner">
          <WifiOff className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("gallery.offlineNotice")}
        </div>
      )}
      <div className="px-5 pt-2 flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl text-leaf-ink leading-tight">{t("gallery.title")}</h1>
          <p className="text-sm text-leaf-mist mt-1">
            {items
              ? (items.length === 1
                  ? t("gallery.countOne", { n: items.length, state: user?.state || "" })
                  : t("gallery.countMany", { n: items.length, state: user?.state || "" }))
              : t("gallery.yourIdsInState")}
          </p>
          {duplicateGroupCount > 0 && (
            <button
              type="button"
              onClick={() => navigate("/duplicates")}
              data-testid="gallery-duplicates-link"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#FFF4DC] hover:bg-[#F8E8B8] border border-[#E9C46A]/50 text-[#8a6a14] text-xs px-3 py-1.5 transition"
            >
              <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />
              {t("gallery.duplicatesToReview", { count: duplicateGroupCount })}
            </button>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <button onClick={() => navigate("/camera")} data-testid="new-scan-button"
            className="hidden md:flex rounded-full px-4 py-2 bg-leaf text-white text-sm items-center gap-1.5 hover:bg-leaf-deep transition">
            <Camera className="w-4 h-4" strokeWidth={1.5} /> {t("gallery.new")}
          </button>
          {items && items.length > 0 && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => {
                  setShareInitialScope(selected.size > 0 ? "selected" : "all");
                  setShareOpen(true);
                }}
                data-testid="share-button"
                className="rounded-full px-3 py-1.5 text-xs flex items-center gap-1.5 border bg-white text-leaf-ink border-[#E8E5DF] hover:bg-parchment transition"
              >
                <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                {t("gallery.share")}
              </button>
              <button
                onClick={selectMode ? exitSelectMode : () => setSelectMode(true)}
                data-testid="select-mode-toggle"
                className={`rounded-full px-3 py-1.5 text-xs flex items-center gap-1.5 border transition ${
                  selectMode ? "bg-leaf text-white border-leaf" : "bg-white text-leaf-ink border-[#E8E5DF] hover:bg-parchment"
                }`}
              >
                <CheckSquare className="w-3.5 h-3.5" strokeWidth={1.5} />
                {selectMode ? t("gallery.cancelSelect") : t("gallery.select")}
              </button>
            </div>
          )}
        </div>
      </div>

      {scoreboard && scoreboard.total > 0 && (
        <div className="px-5 mt-4 hidden md:block" data-testid="gallery-scoreboard">
          <div className="bg-white rounded-3xl border border-[#E8E5DF] p-4 flex flex-wrap gap-x-4 gap-y-2 items-baseline">
            <Stat label={t("gallery.score.native")} value={scoreboard.native} tone="text-leaf" testId="score-native" />
            <Stat label={t("gallery.score.nonNative")} value={scoreboard.nonNative} tone="text-[#8a6a14]" testId="score-non-native" />
            <Stat label={t("gallery.score.weed")} value={scoreboard.weed} tone="text-rust" testId="score-weed" />
            <Stat label={t("gallery.score.keystone")} value={scoreboard.keystone} tone="text-[#4A5D23]" icon={<Sprout className="w-3.5 h-3.5" strokeWidth={1.5} />} testId="score-keystone" />
            <Stat label={t("gallery.score.endangered")} value={scoreboard.endangered} tone="text-rust" icon={<ShieldAlert className="w-3.5 h-3.5" strokeWidth={1.5} />} testId="score-endangered" />
          </div>
        </div>
      )}

      <div className="px-5 mt-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-leaf-mist" strokeWidth={1.5} />
          <input
            ref={searchInputRef}
            type="search"
            enterKeyHint="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
            placeholder={t("gallery.search")}
            data-testid="gallery-search-input"
            aria-label={t("gallery.searchAria")}
            className="w-full bg-white border border-[#E8E5DF] rounded-full pl-11 pr-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-leaf/30 placeholder:text-leaf-mist/70"
          />
          {query && (
            <button onClick={() => setQuery("")} data-testid="gallery-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-leaf-mist hover:text-leaf"
              aria-label={t("gallery.clearSearchAria")}>
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>

      {/* Mobile-only compact control row: Sort + Filter. Per-page moves down
          into the caption row so this line always fits on a narrow screen. */}
      <GalleryMobileControls
        sortKey={sortKey}
        setSortAndPersist={setSortAndPersist}
        sortKey2={sortKey2}
        setSortKey2={setSortKey2}
        secondaryOptions={secondaryOptions}
        filters={filters}
        filterCounts={filterCounts}
        toggleFilter={toggleFilter}
        clearFilters={clearFilters}
        noFiltersActive={noFiltersActive}
      />

      {/* Desktop chip row — hidden on mobile in favor of the compact pill row above.
          Chips toggle independently (multi-select AND semantics); "all" is a
          special chip that clears the entire active set. */}
      <div className="px-5 mt-3 hidden md:block" data-testid="gallery-filter-row">
        <div className="flex flex-wrap gap-2">
          {FILTER_KEYS.map((key) => {
            const active = key === "all" ? noFiltersActive : filters.has(key);
            return (
              <button
                key={key}
                ref={(el) => { chipRefs.current[key] = el; }}
                onClick={() => toggleFilter(key)}
                data-testid={`filter-chip-${key}`}
                aria-pressed={active}
                className={`badge-pill border transition shrink-0 ${active ? "bg-leaf text-white border-leaf" : "bg-white text-leaf-mist border-[#E8E5DF] hover:text-leaf"}`}
              >
                {t(`gallery.filters.${key}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tag filter chips — only rendered when the user has at least one tag.
          Multi-select with AND semantics; URL-persisted via ?tags= so refresh
          + share links preserve the view. Horizontal-scroll on mobile so many
          tags don't stack into multiple rows. On desktop, we cap at the top
          TOP_TAGS_LIMIT most-used tags with a "Show all N" toggle so a power
          user with 40 tags doesn't get a wall of chips. Active tags always
          remain visible even if they'd otherwise be hidden. */}
      {availableTags.length > 0 && (() => {
        const TOP_TAGS_LIMIT = 8;
        const overflow = Math.max(0, availableTags.length - TOP_TAGS_LIMIT);
        // Visible chips: always show active ones + the top-N by frequency,
        // preserving frequency order.
        const visibleTags = (tagsExpanded || overflow === 0)
          ? availableTags
          : availableTags.filter((t, i) => i < TOP_TAGS_LIMIT || activeTags.has(t.key));
        return (
        <div
          className="px-5 mt-3"
          role="group"
          aria-label={t("gallery.tagFilterAria")}
          data-testid="gallery-tag-filter-row"
        >
          <div className="flex md:flex-wrap items-center gap-2 overflow-x-auto md:overflow-visible no-scrollbar -mx-1 px-1">
            <span className="text-[11px] uppercase tracking-[0.14em] text-leaf-mist mr-1 shrink-0">
              {t("gallery.tagFilterLabel")}
            </span>
            {visibleTags.map(({ key, label, count }) => {
              const active = activeTags.has(key);
              if (tagsManageMode) {
                // Manage mode: chip is a "delete this tag from all plants"
                // affordance. Uses rust (danger) tone + trash icon so a
                // user can't confuse it with the filter toggle. Confirm
                // step lives in a follow-up alert dialog.
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTagDeletePending({ key, label, count })}
                    aria-label={t("gallery.tagDeleteChipAria", { tag: label, count })}
                    data-testid={`tag-delete-chip-${key.replace(/\s+/g, "-")}`}
                    className="h-8 rounded-full px-3 text-xs border border-rust/40 bg-rust/5 text-rust hover:bg-rust/10 transition inline-flex items-center gap-1.5 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rust focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={1.75} aria-hidden />
                    <span className="truncate max-w-[16ch]">{label}</span>
                    <span
                      className="text-[10px] text-rust/70 [font-variant-numeric:lining-nums_tabular-nums]"
                      aria-hidden="true"
                    >
                      {count}
                    </span>
                  </button>
                );
              }
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleTag(key)}
                  aria-pressed={active}
                  aria-label={t("gallery.tagFilterChipAria", { tag: label, count })}
                  data-testid={`tag-filter-chip-${key.replace(/\s+/g, "-")}`}
                  className={`h-8 rounded-full px-3 text-xs border transition inline-flex items-center gap-1.5 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2 focus-visible:ring-offset-cream ${
                    active
                      ? "bg-leaf-ink text-cream border-leaf-ink"
                      : "bg-white text-leaf-ink border-[#E8E5DF] hover:bg-parchment"
                  }`}
                >
                  <span className="truncate max-w-[16ch]">{label}</span>
                  <span
                    className={`text-[10px] [font-variant-numeric:lining-nums_tabular-nums] ${active ? "text-cream/80" : "text-leaf-mist"}`}
                    aria-hidden="true"
                  >
                    {count}
                  </span>
                </button>
              );
            })}
            {overflow > 0 && (
              <button
                type="button"
                onClick={() => setTagsExpanded((v) => !v)}
                aria-expanded={tagsExpanded}
                data-testid="tag-filter-show-all"
                className="h-8 rounded-full px-3 text-xs bg-parchment text-leaf-ink border border-[#E8E5DF] hover:bg-[#EEE9DE] transition inline-flex items-center gap-1.5 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
              >
                {tagsExpanded ? t("gallery.tagFilterShowFewer") : t("gallery.tagFilterShowAll", { n: availableTags.length })}
              </button>
            )}
            {/* Manage toggle — enters/leaves "delete tags globally" mode.
                Rendered after the chips so the visual weight lands on the
                filter chips, not the admin action. */}
            <button
              type="button"
              onClick={() => setTagsManageMode((v) => !v)}
              aria-pressed={tagsManageMode}
              data-testid="tag-manage-toggle"
              className={`h-8 rounded-full px-3 text-xs border transition inline-flex items-center gap-1.5 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-cream ${
                tagsManageMode
                  ? "bg-rust text-white border-rust focus-visible:ring-rust"
                  : "bg-white text-leaf-ink border-[#E8E5DF] hover:bg-parchment focus-visible:ring-leaf"
              }`}
            >
              <Pencil className="w-3 h-3" strokeWidth={1.75} aria-hidden />
              {tagsManageMode ? t("gallery.tagManageDone") : t("gallery.tagManage")}
            </button>
            {activeTags.size > 0 && !tagsManageMode && (
              <button
                type="button"
                onClick={clearTags}
                data-testid="tag-filter-clear"
                className="text-[11px] underline underline-offset-4 decoration-leaf/50 hover:decoration-leaf text-leaf-ink ml-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2 focus-visible:ring-offset-cream rounded"
              >
                {t("gallery.tagFilterClear")}
              </button>
            )}
          </div>
          {tagsManageMode && (
            <p className="mt-2 text-[11px] text-rust" data-testid="tag-manage-hint">
              {t("gallery.tagManageHint")}
            </p>
          )}
        </div>
        );
      })()}

      {/* Sort + page-size dropdowns + showing-range status — desktop layout.
          On mobile, the sort/filter controls live in the compact pill row
          above; per-page sits flush-right next to the "showing" caption here. */}
      <div className="px-5 mt-3 flex items-center justify-between gap-3 flex-wrap" data-testid="page-size-row">
        {filtered && filtered.length > 0 ? (
          <p className="text-xs text-leaf-mist truncate min-w-0" data-testid="showing-range">
            {pageSize === 0 || filtered.length <= pageSize
              ? t("gallery.showingAll", { total: filtered.length })
              : t("gallery.showingRange", {
                  start: page * pageSize + 1,
                  end: Math.min(filtered.length, (page + 1) * pageSize),
                  total: filtered.length,
                })}
          </p>
        ) : <span />}
        {/* Mobile per-page — "Per page" label outside the pill; pill shows just the number */}
        <div className="md:hidden shrink-0 flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.14em] text-leaf-mist">
            {t("gallery.perPage")}
          </span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSizeAndPersist(parseInt(v, 10))}>
            <SelectTrigger
              data-testid="page-size-select-mobile"
              aria-label={t("gallery.perPage")}
              className="h-8 w-auto rounded-full border-[#E8E5DF] bg-white text-leaf-ink text-xs px-3 gap-1.5"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)} data-testid={`page-size-option-mobile-${n === 0 ? "all" : n}`}>
                  {n === 0 ? t("gallery.allItems") : n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="hidden md:flex items-center gap-2 flex-wrap justify-end shrink-0">
          <span className="text-[11px] uppercase tracking-[0.16em] text-leaf-mist">{t("gallery.sort.label")}</span>
          <Select
            value={sortKey}
            onValueChange={(v) => setSortAndPersist(v)}
          >
            <SelectTrigger
              data-testid="sort-select"
              aria-label={t("gallery.sort.label")}
              className="h-9 w-[160px] rounded-full border-[#E8E5DF] bg-white text-leaf-ink text-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end" data-testid="sort-options">
              {SORT_KEYS.map((k) => (
                <SelectItem
                  key={k}
                  value={k}
                  data-testid={`sort-option-${k}`}
                >
                  {t(`gallery.sort.options.${k}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-[11px] uppercase tracking-[0.16em] text-leaf-mist">{t("gallery.sort.thenEyebrow")}</span>
          <Select value={sortKey2} onValueChange={setSortKey2}>
            <SelectTrigger
              data-testid="sort-select-secondary"
              aria-label={t("gallery.sort.secondaryLabel")}
              className={`h-9 w-[160px] rounded-full text-sm transition ${
                sortKey2 === "none"
                  ? "border-[#E8E5DF] bg-white text-leaf-mist"
                  : "border-leaf/40 bg-[#E6EFE9] text-leaf-deep"
              }`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end" data-testid="sort-options-secondary">
              {secondaryOptions.map((k) => (
                <SelectItem
                  key={k}
                  value={k}
                  data-testid={`sort-option-secondary-${k}`}
                >
                  {k === "none" ? t("gallery.sort.secondaryNone") : t(`gallery.sort.options.${k}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-[11px] uppercase tracking-[0.16em] text-leaf-mist">{t("gallery.perPage")}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => setPageSizeAndPersist(parseInt(v, 10))}
          >
            <SelectTrigger
              data-testid="page-size-select"
              aria-label={t("gallery.perPage")}
              className="h-9 w-[88px] rounded-full border-[#E8E5DF] bg-white text-leaf-ink text-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end" data-testid="page-size-options">
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem
                  key={n}
                  value={String(n)}
                  data-testid={`page-size-option-${n === 0 ? "all" : n}`}
                >
                  {n === 0 ? t("gallery.allItems") : n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {(() => {
        // "Mark all reviewed" convenience button appears only when the current
        // filtered/sorted list contains at least one plant still awaiting review.
        // Acts on the full filtered set, not just the current page — the point
        // is to burn down the "needs ID" queue in one click.
        if (!filtered || selectMode) return null;
        const unreviewedIds = filtered.filter((it) => it.needs_revisit === true).map((it) => it.scan_id);
        if (unreviewedIds.length === 0) return null;
        return (
          <div className="px-5 mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => setPendingMarkReviewed({ ids: unreviewedIds, source: "header" })}
              data-testid="mark-all-reviewed-button"
              className="text-xs inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-leaf/40 bg-[#E6EFE9] text-leaf-deep hover:bg-[#DAE8DE] transition"
            >
              <CheckSquare className="w-3.5 h-3.5" strokeWidth={1.5} />
              {t("gallery.markAllReviewed", { n: unreviewedIds.length })}
            </button>
          </div>
        );
      })()}

      <div className="px-5 mt-4">
        {filtered === null ? (
          <SkeletonGrid />
        ) : filtered.length === 0 ? (
          items.length === 0
            ? <EmptyState onStart={() => navigate("/camera")} t={t} />
            : <NoMatch onClear={() => { setQuery(""); clearFilters(); }} t={t} />
        ) : (
          <div className={selectedScan ? "lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-6 lg:items-start" : ""}>
            <div className="min-w-0">
              <div
                className={
                  selectedScan
                    ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-3"
                    : "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"
                }
                data-testid="gallery-grid"
              >
                {paged.items.map((it) => (
                  <PlantCard
                    key={it.scan_id}
                    item={it}
                    userLang={user?.language}
                    userState={user?.state}
                    onRequestDelete={(id) => setPendingDelete(id)}
                    selectMode={selectMode}
                    selected={selected.has(it.scan_id)}
                    onToggleSelect={toggleSelect}
                    onSelectInline={isDesktop && !selectMode ? openInline : null}
                    activeInline={selectedId === it.scan_id}
                    onTagClick={toggleTag}
                    activeTags={activeTags}
                    t={t}
                  />
                ))}
              </div>
              {paged.totalPages > 1 && (
                <Pagination
                  page={page}
                  totalPages={paged.totalPages}
                  onChange={setPage}
                  t={t}
                />
              )}
            </div>
            {selectedScan && (
              <aside
                className="hidden lg:block lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:overscroll-contain no-scrollbar"
                data-testid="detail-pane-column"
              >
                <PlantDetailPane
                  scan={selectedScan}
                  userLang={user?.language}
                  onClose={closeInline}
                />
              </aside>
            )}
          </div>
        )}
      </div>

      {/* All four confirmation dialogs (single-delete, bulk-delete, bulk-merge
          with species-mismatch warning, mark-reviewed) extracted to
          GalleryDialogs.jsx (iter56). Behavior-preserving. */}
      <GalleryDialogs
        pendingDelete={pendingDelete}
        onOpenChangeDelete={(o) => !o && setPendingDelete(null)}
        onConfirmDelete={() => pendingDelete && remove(pendingDelete)}
        pendingBulkDelete={pendingBulkDelete}
        onOpenChangeBulkDelete={setPendingBulkDelete}
        onConfirmBulkDelete={bulkRemove}
        pendingBulkMerge={pendingBulkMerge}
        onOpenChangeBulkMerge={setPendingBulkMerge}
        onConfirmBulkMerge={bulkMerge}
        merging={merging}
        pendingMarkReviewed={pendingMarkReviewed}
        onOpenChangeMarkReviewed={(o) => !o && setPendingMarkReviewed(null)}
        onConfirmMarkReviewed={markReviewedRun}
        items={items}
        selected={selected}
      />

      {/* Photo picker — only shown when the merge candidate pool would
          exceed MAX_EXTRA_PHOTOS_FE. Primary's own extras stay untouched;
          user picks up to (5 - primaryExtras) photos from the other scans. */}
      <MergePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        loading={pickerLoading}
        primary={pickerPrimary}
        candidates={pickerCandidates}
        selection={pickerSelection}
        onToggleCandidate={togglePickerCandidate}
        onCancel={() => setPickerOpen(false)}
        onConfirm={bulkMerge}
        merging={merging}
      />

      {/* Bottom select-mode action bar — behavior-preserving split out to
          GallerySelectActionBar (mobile-stack on <md, one-row on md+). */}
      {selectMode && (
        <GallerySelectActionBar
          selected={selected}
          paged={paged}
          items={items}
          merging={merging}
          onSelectAllOnPage={selectAllOnPage}
          onOpenBulkShare={() => { setShareInitialScope("selected"); setShareOpen(true); }}
          onOpenBulkTag={() => setBulkTagOpen(true)}
          onOpenBulkCorridor={() => setBulkCorridorOpen(true)}
          onOpenBulkMarkReviewed={(ids) => setPendingMarkReviewed({ ids, source: "bulk" })}
          onMergeIntent={handleMergeIntent}
          onOpenBulkDelete={() => setPendingBulkDelete(true)}
          onExit={exitSelectMode}
        />
      )}

      {/* Confirm dialog for the "delete tag globally" affordance in the
          tag-filter row. Explicit action label + rust color so users
          understand this touches every scan with the tag. */}
      <AlertDialog
        open={!!tagDeletePending}
        onOpenChange={(o) => !tagDeleteBusy && !o && setTagDeletePending(null)}
      >
        <AlertDialogContent data-testid="tag-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">
              {t("gallery.tagDeleteTitle", { tag: tagDeletePending?.label || "" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("gallery.tagDeleteDesc", {
                count: tagDeletePending?.count || 0,
                tag: tagDeletePending?.label || "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={tagDeleteBusy} data-testid="tag-delete-cancel">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDeleteTagGlobally(); }}
              disabled={tagDeleteBusy}
              data-testid="tag-delete-confirm-button"
              className="bg-rust hover:bg-rust/90"
            >
              {tagDeleteBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
              )}
              {t("gallery.tagDeleteConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkTagDialog
        open={bulkTagOpen}
        onOpenChange={setBulkTagOpen}
        scanIds={Array.from(selected)}
        allItems={items || []}
        onDone={(count, appliedTag, mode = "add") => {
          if (mode === "remove") {
            // Optimistic: strip the removed tags (comma-joined string in `appliedTag`)
            // from every selected item's tag list.
            const removed = String(appliedTag || "")
              .split(",")
              .map((x) => x.trim().toLowerCase())
              .filter(Boolean);
            if (removed.length > 0) {
              setItems((prev) => (prev || []).map((it) => {
                if (!selected.has(it.scan_id)) return it;
                const keep = (it.tags || []).filter(
                  (x) => !removed.includes(String(x).toLowerCase())
                );
                return { ...it, tags: keep };
              }));
            }
            setBulkTagOpen(false);
            toast.success(t("gallery.toastBulkUntagged", { n: count }));
            setSelected(new Set());
            setSelectMode(false);
            return;
          }
          // Optimistically update tags on affected items so the UI reflects the change immediately.
          if (count > 0 && appliedTag) {
            setItems((prev) => (prev || []).map((it) => {
              if (!selected.has(it.scan_id)) return it;
              const existing = (it.tags || []).map((x) => String(x).toLowerCase());
              if (existing.includes(appliedTag.toLowerCase())) return it;
              return { ...it, tags: [...(it.tags || []), appliedTag] };
            }));
          }
          setBulkTagOpen(false);
          if (count > 0) {
            toast.success(t("gallery.toastBulkTagged", { n: count }));
            setSelected(new Set());
            setSelectMode(false);
          } else {
            toast.message(t("gallery.toastBulkTagNoop"));
          }
        }}
      />

      <BulkCorridorDialog
        open={bulkCorridorOpen}
        onOpenChange={setBulkCorridorOpen}
        scanIds={Array.from(selected)}
        allItems={items || []}
        onDone={(data) => {
          const added = data?.added ?? 0;
          const failed = Array.isArray(data?.failed) ? data.failed.length : 0;
          setBulkCorridorOpen(false);
          if (added > 0) {
            toast.success(t("gallery.toastBulkCorridor", { n: added }));
          }
          if (failed > 0) {
            toast.error(t("gallery.toastBulkCorridorPartial", { n: failed }));
          }
          setSelected(new Set());
          setSelectMode(false);
        }}
      />


      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        selectedIds={Array.from(selected)}
        allScanCount={items?.length || 0}
        initialScope={shareInitialScope}
      />
    </AppShell>
  );
}
